import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Button, ChevronDownIcon, getSemanticValue } from '@freenow/wave';
import { OneLineText } from './OneLineText';

const buttonVariants = {
    primary: {
        color: getSemanticValue('foreground-on-background-primary'),
        fill: getSemanticValue('foreground-on-background-primary'),
        background: getSemanticValue('background-element-primary-default'),

        '&:hover': {
            color: getSemanticValue('foreground-on-background-accent'),
            background: getSemanticValue('background-element-primary-emphasized'),
        },

        '&:disabled': {
            color: getSemanticValue('foreground-on-background-disabled'),
            background: getSemanticValue('background-element-disabled-default'),
        },
    },
    secondary: {
        color: getSemanticValue('foreground-primary'),
        fill: getSemanticValue('foreground-primary'),
        background: getSemanticValue('background-page-default'),
        borderColor: getSemanticValue('border-neutral-default'),
        borderStyle: 'solid', // Added for border to be visible
        borderWidth: '1px', // Added for border to be visible

        '&:hover': {
            color: getSemanticValue('foreground-on-background-neutral'),
            background: getSemanticValue('background-element-neutral-emphasized'),
        },

        '&:disabled': {
            color: getSemanticValue('foreground-disabled'),
            fill: getSemanticValue('foreground-disabled'),
            background: getSemanticValue('transparent'),
            borderColor: getSemanticValue('border-disabled'),
        },
    },
};

const StyledDropdownButton = styled(Button)`
    min-width: 0;
    max-width: 100%;

    ${({ variant }) => buttonVariants[variant]}
`;

const ButtonWrapper = styled.div`
    position: relative;
    vertical-align: middle;
`;

export enum DROP_DOWN_PLACEMENT {
    LEFT = 'left',
    RIGHT = 'right',
}

const DropdownWrapper = styled.div`
    position: absolute;
    background-color: ${getSemanticValue('background-page-elevation-1')};
    border-radius: 4px;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
    padding: 8px 0;
    margin-top: 4px;
    overflow-y: auto;
    z-index: 1;
    text-align: left;

    ${(props) =>
        props.placement === DROP_DOWN_PLACEMENT.LEFT &&
        css`
            left: 2px;
        `}

    ${(props) =>
        props.placement === DROP_DOWN_PLACEMENT.RIGHT &&
        css`
            right: 2px;
        `}
`;

export const DropdownItem = styled.div`
    padding: 8px 16px;
    font-size: 14px;
    white-space: nowrap;

    color: ${(props) =>
        props.disabled ? getSemanticValue('foreground-disabled') : getSemanticValue('foreground-primary')};

    &:hover {
        cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
        background-color: ${(props) =>
            props.disabled ? 'transparent' : getSemanticValue('background-element-accent-faded')};
    }
`;

interface DropdownButtonProps {
    title: string | React.ReactNode;
    children: React.ReactNode;
    placement?: DROP_DOWN_PLACEMENT;
    buttonSize?: string;
    disabled?: boolean;
    variant?: 'primary' | 'secondary';
}

export const DropdownButton = ({
    title,
    children,
    placement = DROP_DOWN_PLACEMENT.LEFT,
    buttonSize = 'small',
    disabled = false,
    variant = 'secondary',
}: DropdownButtonProps) => {
    const [open, setOpen] = useState(false);
    const dropdownButtonRef = useRef<HTMLDivElement>(null);

    const toggleOpen = () => setOpen(!open);

    useEffect(() => {
        const handleClickOutside = ({ target }) => {
            if (dropdownButtonRef.current && !dropdownButtonRef.current.contains(target)) {
                setImmediate(() => setOpen(false));
            }
        };

        document.addEventListener('click', handleClickOutside, true);

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [setOpen]);

    return (
        <ButtonWrapper>
            <div ref={dropdownButtonRef}>
                <StyledDropdownButton
                    onClick={toggleOpen}
                    variant={variant}
                    size={buttonSize}
                    data-testid="dropdown-button"
                    disabled={disabled}
                >
                    <OneLineText fontSize={1} title={title} mr={1}>
                        {title}
                    </OneLineText>
                    <ChevronDownIcon size={18} />
                </StyledDropdownButton>
            </div>
            {open ? <DropdownWrapper placement={placement}>{children}</DropdownWrapper> : null}
        </ButtonWrapper>
    );
};
